import { ArticleFields, ArticleMetadataFields, type IArticle } from '@pressreader/content-types';
import { hasFlag, hasNonEmptyFlag } from '@pressreader/utils';

type IssueKnowledgeArticle = Pick<IArticle, 'issue' | 'missingMetadata' | 'restrictedFields'>;

/**
 * Maps an article metadata flag to the corresponding article field when the metadata is marked as missing.
 */
export function mapMissingMetadataToArticleField(
    missingMetadata: ArticleMetadataFields | undefined,
    metadataField: ArticleMetadataFields = ArticleMetadataFields.Issue,
): ArticleFields | undefined {
    if (!hasNonEmptyFlag(missingMetadata, metadataField)) {
        return undefined;
    }

    if (metadataField === ArticleMetadataFields.Issue) {
        return ArticleFields.Issue;
    }

    return undefined;
}

/**
 * Checks whether the issue metadata is known to be present.
 */
export function isIssueKnownPresent(article: Pick<IssueKnowledgeArticle, 'issue'>): boolean {
    return !!article.issue?.issueKey;
}

/**
 * Checks whether the issue metadata is known to be either missing or restricted.
 * Missing state can be represented by either:
 * - metadata-level marker (`missingMetadata`)
 * - requested-but-unavailable field marker (`restrictedFields`)
 */
export function isIssueKnownUnavailable(article: Pick<IssueKnowledgeArticle, 'missingMetadata' | 'restrictedFields'>): boolean {
    const missingIssueField = mapMissingMetadataToArticleField(article.missingMetadata) ?? ArticleFields.None;
    return hasFlag(article.restrictedFields | missingIssueField, ArticleFields.Issue);
}

/**
 * Checks whether the issue metadata state is resolved (present or explicitly unavailable).
 */
export function isIssueKnowledgeResolved(article: IssueKnowledgeArticle): boolean {
    return isIssueKnownPresent(article) || isIssueKnownUnavailable(article);
}

/**
 * Returns a bitmask of article fields that are known to be missing and should not be requested again.
 * Includes already restricted article fields and metadata-driven missing issue field.
 */
export function getKnownMissingArticleFields(
    article: IssueKnowledgeArticle,
    {
        resetRestricted,
    }: {
        // should already restricted fields be included in the result, default true
        resetRestricted?: boolean;
    } = {},
): ArticleFields {
    const missingIssueField = mapMissingMetadataToArticleField(article.missingMetadata) ?? ArticleFields.None;
    return (resetRestricted ? ArticleFields.None : article.restrictedFields) | missingIssueField;
}
